import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { Brand, PoweredBy } from '@/components/BrandMark';

/** The desk's floor; the server enforces it too, with the obvious ones refused. */
const MIN_LENGTH = 10;

/**
 * Shown instead of the app while the account must choose its own password —
 * a password HR issued or reset. The server refuses every other request until
 * then, so there is nothing else to show.
 *
 * Saving ends every session, this one included, and lands on the sign-in page:
 * signing in once with the new password is the proof it was typed as meant.
 */
export function ChangePasswordPage() {
  const { t } = useTranslation();
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const problem =
    next && next.length < MIN_LENGTH
      ? t('auth.password_rule', { count: MIN_LENGTH })
      : confirm && confirm !== next
        ? t('The two new passwords do not match')
        : next && current && next === current
          ? t('Choose a password different from the one you were given')
          : null;
  const ready = Boolean(current && next && confirm) && !problem;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Stack spacing={2} sx={{ width: 420, maxWidth: '100%' }}>
        <Card>
          <CardContent component="form" onSubmit={submit}>
            <Stack spacing={2.5}>
              <Brand size={40} maxWidth={240} titleVariant="h5" />
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  {t('Choose your own password')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('{{name}}, the password you signed in with was set for you. Choose your own to continue; you will then sign in again with it.', {
                    name: user?.fullName ?? '',
                  })}
                </Typography>
              </Stack>

              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label={t('Current password')}
                type="password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                autoComplete="current-password"
                required
                autoFocus
              />
              <TextField
                label={t('auth.new_password')}
                type="password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                autoComplete="new-password"
                helperText={t('auth.password_rule', { count: MIN_LENGTH })}
                required
              />
              <TextField
                label={t('Confirm the new password')}
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                error={Boolean(problem) && Boolean(confirm)}
                helperText={confirm ? problem ?? ' ' : ' '}
                required
              />
              <Button type="submit" variant="contained" size="large" disabled={!ready || busy}>
                {busy ? t('Saving…') : t('auth.change_password')}
              </Button>
              <Button size="small" color="inherit" onClick={() => void logout()} disabled={busy}>
                {t('auth.sign_out')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
        <PoweredBy />
      </Stack>
    </Box>
  );
}
